<template>
  <div class="c-gen-nav__desktop-menus">
    <ul class="c-gen-nav__menus">
      <li
        v-for="(menu, i) in menus"
        :key="i"
        class="c-gen-nav__menu-item"
        @mouseenter="onMenuEnter(menu)"
        @mouseleave="onMenuLeave"
      >
        <SiteLink
          class="c-gen-nav__menu-link"
          :class="{
            'is-active': activeMenuTitle === utils.sc.getFieldValue(menu, 'title'),
            'is-disabled': !hasLink(menu),
          }"
          :link="getLinkField(menu)"
          v-bind="getLinkAttrs(menu)"
          @click="onLinkClick($event, menu)"
        >
          {{ utils.sc.getFieldValue(menu, 'title') }}
        </SiteLink>

        <transition name="slide-down">
          <div
            v-if="hasCategories(menu) && activeMenuTitle === utils.sc.getFieldValue(menu, 'title')"
            class="c-gen-nav__mega-menu"
          >
            <div class="c-gen-nav__mega-menu-inner">
              <div class="c-gen-nav__mega-media">
                <img
                  v-if="getMegaImageSrc(menu)"
                  :src="getMegaImageSrc(menu)"
                  :alt="getMegaImageAlt(menu)"
                  loading="lazy"
                />
              </div>

              <div class="c-gen-nav__mega-columns">
                <div class="c-gen-nav__mega-column">
                  <SiteLink
                    v-for="cat in getCategories(menu)"
                    :key="utils.sc.getFieldValue(cat, 'title')"
                    class="c-gen-nav__mega-card"
                    :class="{
                      'is-active': hoveredCategory === getTitle(cat),
                      'is-disabled': !hasLink(cat),
                    }"
                    :link="getLinkField(cat)"
                    v-bind="getLinkAttrs(cat)"
                    @click="onLinkClick($event, cat)"
                    @mouseenter="setHoveredCategory(cat)"
                    @focus="setHoveredCategory(cat)"
                  >
                    <span>{{ getTitle(cat) }}</span>
                    <Icon
                      v-if="hasItems(cat)"
                      name="arrow-right"
                      class="c-gen-nav__icon c-gen-nav__mega-card-arrow"
                    />
                  </SiteLink>
                </div>

                <div v-if="activeCategoryItems.length" class="c-gen-nav__mega-column">
                  <SiteLink
                    v-for="item in activeCategoryItems"
                    :key="utils.sc.getFieldValue(item, 'title')"
                    class="c-gen-nav__mega-card"
                    :class="{
                      'is-active': hoveredItem === getTitle(item),
                      'is-disabled': !hasLink(item),
                    }"
                    :link="getLinkField(item)"
                    v-bind="getLinkAttrs(item)"
                    @click="onLinkClick($event, item)"
                    @mouseenter="setHoveredItem(item)"
                    @focus="setHoveredItem(item)"
                  >
                    <span>{{ getTitle(item) }}</span>
                    <Icon
                      v-if="hasSubItems(item)"
                      name="arrow-right"
                      class="c-gen-nav__icon c-gen-nav__mega-card-arrow"
                    />
                  </SiteLink>
                </div>

                <div class="c-gen-nav__mega-column" :class="{ 'is-empty': !activeSubItems.length }">
                  <SiteLink
                    v-for="sub in activeSubItems"
                    :key="utils.sc.getFieldValue(sub, 'title')"
                    class="c-gen-nav__mega-link"
                    :class="{ 'is-disabled': !hasLink(sub) }"
                    :link="getLinkField(sub)"
                    v-bind="getLinkAttrs(sub)"
                    @click="onLinkClick($event, sub)"
                  >
                    {{ getTitle(sub) }}
                  </SiteLink>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import utils from '@/utils';
import { appendQuery, getBetterUrl } from '@/utils/uri-utils';
import * as querystring from 'querystring';
import { HeaderProps } from '@/types/components/Header';
import SiteLink from '@/elements/SiteLink.vue';

const props = defineProps<HeaderProps>();
const menus = computed(() => props.fields?.menus ?? []);

const activeMenuTitle = ref('');
const hoveredCategory = ref('');
const hoveredItem = ref('');
let leaveTimer: ReturnType<typeof setTimeout> | null = null;

const onMenuEnter = (menu: any) => {
  if (leaveTimer) {
    clearTimeout(leaveTimer);
    leaveTimer = null;
  }

  activeMenuTitle.value = utils.sc.getFieldValue(menu, 'title');

  const firstCategory = getCategories(menu)[0];
  hoveredCategory.value = firstCategory ? getTitle(firstCategory) : '';

  const firstItem = firstCategory ? getItems(firstCategory)[0] : null;
  hoveredItem.value = firstItem ? getTitle(firstItem) : '';
};

const onMenuLeave = () => {
  leaveTimer = setTimeout(() => {
    activeMenuTitle.value = '';
    hoveredCategory.value = '';
    hoveredItem.value = '';
  }, 150);
};

const activeMenu = computed(() =>
  menus.value.find((menu) => utils.sc.getFieldValue(menu, 'title') === activeMenuTitle.value)
);

const activeCategory = computed(() =>
  getCategories(activeMenu.value).find((cat) => getTitle(cat) === hoveredCategory.value)
);

const activeCategoryItems = computed(() => getItems(activeCategory.value));

const activeItem = computed(() =>
  activeCategoryItems.value.find((item) => getTitle(item) === hoveredItem.value)
);

const activeSubItems = computed(() => getSubItems(activeItem.value));

const setHoveredCategory = (cat: any) => {
  hoveredCategory.value = getTitle(cat);

  const firstItem = getItems(cat)[0];
  hoveredItem.value = firstItem ? getTitle(firstItem) : '';
};

const setHoveredItem = (item: any) => {
  hoveredItem.value = getTitle(item);
};

const getCategories = (menu: any) => {
  const f = menu?.fields || menu || {};
  const c = f.categories;
  return Array.isArray(c) ? c : [];
};

const hasCategories = (menu: any) => {
  return getCategories(menu).length > 0;
};

const getItems = (cat: any) => {
  const f = cat?.fields || cat || {};
  const i = f.items;
  return Array.isArray(i) ? i : [];
};

const hasItems = (cat: any) => {
  return getItems(cat).length > 0;
};

const getSubItems = (item: any) => {
  const f = item?.fields || item || {};
  const s = f.subItems;
  return Array.isArray(s) ? s : [];
};

const hasSubItems = (item: any) => {
  return getSubItems(item).length > 0;
};

const getTitle = (item: any) => {
  const title = utils.sc.getFieldObj(item, 'title');
  return title?.value || title || item?.displayName || item?.name || '';
};

const getLinkHref = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  let url = link?.value?.href || link?.href || '';

  if (!url) return '';
  if (/^(mailto|tel):/gi.test(url)) return url;
  if (/^https?:\/\/((tel|mailto):|#)/gi.test(url)) return url.replace(/^https?:\/\//gi, '');

  const queryString = link?.value?.querystring || link?.querystring;
  if (queryString) {
    try {
      url = appendQuery(url, querystring.parse(queryString));
    } catch (error) {
      console.warn('Failed to parse querystring:', queryString, error);
    }
  }

  const anchor = link?.value?.anchor || link?.anchor;
  if (anchor) {
    url = `${url}?id=${anchor}`;
  }

  return getBetterUrl(url) ?? '';
};

const hasLink = (item: any) => Boolean(getLinkHref(item));

const getLinkField = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  return {
    value: {
      ...(link?.value || link || {}),
      href: link?.value?.href || link?.href || '',
    },
  };
};

const onLinkClick = (event: MouseEvent, item: any) => {
  if (hasLink(item)) return;
  event.preventDefault();
};

const getLinkAttrs = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  const value = link?.value || link || {};
  const attrs: Record<string, string> = {};

  if (!hasLink(item)) attrs['aria-disabled'] = 'true';
  if (value.target && hasLink(item)) attrs.target = value.target;
  if (value.title) attrs.title = value.title;
  if (value.class) attrs.class = value.class;
  if (value.target === '_blank' && hasLink(item)) attrs.rel = 'noopener noreferrer';

  return attrs;
};

const getMegaImage = (menu: any) => utils.sc.getFieldObj(menu, 'megaImage');

const getMegaImageSrc = (menu: any) => {
  const image = getMegaImage(menu);
  return image?.value?.src || image?.src || '';
};

const getMegaImageAlt = (menu: any) => {
  const image = getMegaImage(menu);
  return image?.value?.alt || image?.alt || '';
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-gen-nav {
  &__desktop-menus {
    display: none;
    flex: 1;

    @include tablet-landscape {
      display: flex;
      justify-content: center;
    }
  }

  &__menus {
    display: flex;
    gap: 45px;
    align-items: center;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__menu-item {
    position: relative;
    display: flex;
  }

  &__menu-link {
    display: block;
    padding: 4px 0 10px;
    font-weight: 400;
    font-size: 16px;
    color: $white;
    transition:
      opacity 0.2s,
      border-color 0.2s;
    border-bottom: 3px solid transparent;
    text-decoration: none;

    &:hover,
    &.is-active {
      border-bottom-color: $white;
      opacity: 1;
      font-weight: 500;
    }
  }

  .slide-down-enter-active,
  .slide-down-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .slide-down-enter-from,
  .slide-down-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }

  &__mega-menu {
    position: fixed;
    top: 69px;
    left: 0;
    right: 0;
    width: 100%;
    background: $white;
    box-shadow: 0 12px 34px rgba(0, 0, 0, 0.08);
    z-index: 999;
  }

  &__mega-menu-inner {
    --mega-menu-content-height: min(330px, calc(100vh - 140px));

    display: grid;
    grid-template-columns: minmax(240px, 270px) minmax(0, 1fr);
    gap: 40px;
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    padding: 24px 24px 28px;
  }

  &__mega-media {
    width: 100%;
    min-width: 240px;
    max-width: 270px;
    overflow: hidden;
    border-radius: 8px;
    // background: #eef2ef;
    img {
      display: block;
      width: 100%;
    }
  }

  &__mega-columns {
    display: grid;
    grid-template-columns: 360px 360px minmax(0, 360px);
    gap: 0;
    align-content: flex-start;
    justify-content: start;
    min-width: 0;
    width: auto;
    max-width: 100%;
    height: var(--mega-menu-content-height);
    overflow: hidden;
  }

  &__mega-column {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
    height: 100%;
    padding: 0 20px;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-gutter: stable;
    border-right: 1px solid #e6ebe8;
    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      border-right: 0;
      padding-right: 0;
    }

    &.is-empty {
      visibility: hidden;
      pointer-events: none;
    }
  }

  &__mega-column:nth-child(2) &__mega-card {
    font-size: 14px;
  }

  &__mega-card,
  &__mega-link {
    width: 100%;
    border: 0;
    background: transparent;
    color: #2f3033 !important;
    font: inherit;
    text-align: left;
    cursor: default;

    &:not(.is-disabled) {
      cursor: pointer;
    }
  }

  &__mega-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 52px;
    padding: 15px 18px;
    border-radius: 4px;
    background: #f4f4f4;
    font-size: 16px;
    line-height: 1.2;
    font-weight: 600;
    letter-spacing: 0.01em;
    transition:
      background-color 0.18s ease,
      color 0.18s ease;

    span {
      min-width: 0;
      word-break: break-word;
    }

    &:hover,
    &:focus,
    &.is-active {
      background: #eef3ef;
      color: #6e9874;
      outline: none;
    }
  }

  &__mega-card-arrow {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    color: currentColor;
  }

  &__mega-link {
    min-height: 48px;
    padding: 12px 18px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.3;
    font-weight: 400;
    transition:
      background-color 0.18s ease,
      color 0.18s ease;

    &:hover,
    &:focus {
      background: #eef3ef;
      color: #6e9874;
      outline: none;
    }
  }
}

@media (min-width: 1200px) and (max-width: 1439px) {
  .c-gen-nav {
    &__menus {
      gap: 30px;
    }

    &__menu-link {
      font-size: 16px;
    }

    &__mega-menu-inner {
      gap: 36px;
      padding: 24px 24px 28px;
    }

    &__mega-columns {
      grid-template-columns: 300px 300px minmax(0, 260px);
    }

    &__mega-card {
      min-height: 52px;
      padding: 15px 18px;
      font-size: 16px;
    }

    &__mega-link {
      min-height: 52px;
      padding: 15px 18px;
      font-size: 12px;
    }
  }
}
</style>
